import type {
  CanvasDatabasePresentationState,
  ColumnDataType,
  DatabaseBlockModel,
} from '@blocksuite/affine-model';
import {
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  type DatabaseShardManifestEntry,
  normalizeDatabaseShardManifest,
} from '@blocksuite/affine-model';
import { getSelectedModelsCommand } from '@blocksuite/affine-shared/commands';
import {
  FeatureFlagService,
  NotificationProvider,
} from '@blocksuite/affine-shared/services';
import { type InsertToPosition } from '@blocksuite/affine-shared/utils';
import {
  assertDatabaseColumnShapeWithinLimits,
  canProjectSourceWindow,
  type DatabaseFlags,
  type DatabaseProjectionQuery,
  type DatabaseProjectionSource,
  type DatabaseProjectionSubscriptionResult,
  DataSourceBase,
  type DataSourceCellsWindow,
  type DataSourceCellsWindowRequest,
  type DataSourceRowsWindow,
  type DataSourceTableViewLayoutState,
  type DataSourceViewLayoutState,
  type DataSourceViewWindowState,
  type DataViewDataType,
  dataViewI18nT,
  projectionCacheKey,
  type PropertyDuplicateOptions,
  type PropertyMetaConfig,
  ROW_SHARDED_DATABASE_GATE_MESSAGE,
  type TypeInstance,
  type ViewManager,
  type ViewMeta,
} from '@blocksuite/data-view';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import type { EditorHost } from '@blocksuite/std';
import { type Store } from '@blocksuite/store';
import {
  computed,
  effect,
  type ReadonlySignal,
  signal,
} from '@preact/signals-core';
import type {
  DatabaseBulkProgress,
  DatabaseBulkResult,
} from '@streamlain/database-identity';
import {
  assertDatabaseIdentity,
  classifyPageSubdocId,
  DATABASE_PRODUCT_LIMITS,
  DatabaseColumnCellKeyCollisionError,
  databaseProjectionViewState,
  databaseShardDirectoryShardRowStart,
  FILE_INDEX_MANAGED_BY,
  isDatabaseLimitError,
  type ResolvedDatabaseIdentity,
  selectDatabaseShardsMayContainRows,
  storedDatabaseShardDirectory,
  trackDatabaseShardDirectory,
} from '@streamlain/database-identity';
import type { LocalPublicationReceipt } from '@streamlain/doc-publication-contract';
import { PublicationError } from '@streamlain/doc-publication-contract';
import type {
  DatabaseImportCommit,
  DatabaseImportCommitSummary,
  DatabaseImportPort,
} from '@streamlain/doc-publication-contract/database-writer';

import { createCanvasLivePresentationState } from './canvas-presentation-state.js';
import {
  databaseBulkProviders,
  type DatabaseClientBulkPort,
  DatabaseClientBulkReceiptOwner,
  type DatabaseClientBulkRequest,
  executeDatabaseClientBulk,
} from './data-source-bulk.js';
import type {
  DatabasePublicationScope,
  DatabaseRowMutation,
} from './data-source-contracts.js';
import {
  type DatabasePublicationViewObserver,
  type DatabasePublicationViewState,
  type DatabaseRowCellValueInsert,
  type DatabaseRowSet,
  type DatabaseRowShardDocLoadReason,
  type DatabaseRowShardReadiness,
  type DatabaseRowShardRepairResult,
  type DatabaseRowShardWriteBarrier,
  type DatabaseSystemDocMissingObserver,
  type DatabaseUiStatePersistence,
  type DataSourceWriteActor,
  type ReactiveShardedCellValue,
  type ShardedRowRecord,
  type ShardedRowsDocCacheEntry,
  type SpacialProperty,
} from './data-source-contracts.js';
import { DatabaseLocalUiController } from './data-source-local-ui.js';
import {
  databaseProjectionConsumerKey,
  DatabaseProjectionController,
  type DatabaseProjectionProvider,
  databaseProjectionProviders,
  emptyDatabaseProjection,
} from './data-source-projection.js';
import {
  provisionShardedDatabaseHostBlock,
  type ShardedDatabaseBlockDataSourceProvisionInput,
  type ShardedDatabaseBlockDataSourceProvisionResult,
} from './data-source-provisioning.js';
import {
  type DatabaseWriterPorts,
  registerDatabaseWriterPorts,
} from './data-source-publication.js';
import { createDatabaseSourceRuntime } from './data-source-runtime.js';
import type {
  DatabaseShardDocument,
  DatabaseSourceRuntime,
} from './data-source-runtime-context.js';
import {
  type DatabaseShardDirectoryCompleteRead,
  requireLoadedShardDirectory,
  shownShardDirectoryRowCount,
} from './data-source-shard-directory.js';
import {
  disposeRowShardWrites,
  flushRowShardWrites,
  releaseWindowShardDocuments,
  setWindowShardDocuments,
} from './data-source-shard-ownership.js';
import {
  applyArchivedImport,
  applyImportedCommits,
  assertNoAtomicRowMutation,
  atomicDatabaseRootChange,
  claimRowPublication,
  currentWriterId,
  type DatabaseRowSetGroupPlan,
  disposeDirectoryCompleteWait,
  getDatabaseRootReadModel,
  openPublicationScope,
  planRowSetGroup,
  queueDatabaseRootMutation,
  releaseRowWriterSessions,
} from './data-source-shard-writing.js';
import {
  findLocalRowShards,
  flushLocalRowPublications,
  writePreparedShardedCellsToLocalDocs,
  writeShardedRowCellsToLocalDocs,
} from './data-source-shard-writing.js';
import {
  observeDatabaseRootAgent,
  resolveDatabaseStorageModel,
} from './data-source-storage.js';
import {
  FILE_INDEX_ROOT_SCHEMA_HARD_LIMIT_BYTES,
  FILE_INDEX_VIEW_HARD_LIMIT,
  INITIAL_DATABASE_ROW_WINDOW_LIMIT,
  isFileIndexManagedColumnData,
  isFileIndexSystemColumnId,
  isSystemManagedPropertyData,
  registerDatabasePublicationViewObserver,
  registerDatabaseSystemDocMissingObserver,
  registerRowShardBarrier,
  rowShardWriteBarriers,
} from './data-source-values.js';
import { DatabaseBlockViewManager } from './data-source-view-manager.js';
import { databaseLimitNoticeText } from './database-limit-notice.js';
import {
  getFileIndexShardRegistry,
  readFileIndexShardEntries,
} from './file-index-shard-registry.js';
import { databaseBlockProperties } from './properties/index.js';
import { getOrderedViews, getViewDataForRead } from './utils/block-utils.js';
import {
  databaseBlockViewConverts,
  getCreatableDatabaseBlockViews,
} from './views/index.js';

export type {
  DatabasePublicationPort,
  DatabaseRelationInverseEdit,
  DatabaseRelationInversePlanner,
  DatabaseRowMutation,
} from './data-source-contracts.js';
export type { DatabaseRowShardDocLoadReason } from './data-source-contracts.js';
export type { DatabaseRowShardDocLoadContext } from './data-source-contracts.js';
export type { DatabaseRowShardHydrationProgress } from './data-source-contracts.js';
export type { DatabaseRowShardReadiness } from './data-source-contracts.js';
export type { DatabaseRowShardRepairResult } from './data-source-contracts.js';
export type {
  DatabasePublicationViewObserver,
  DatabasePublicationViewState,
  DatabaseRowShardDocLoader,
  DatabaseSystemDocMissingObserver,
} from './data-source-contracts.js';
export type { DatabaseRowShardSnapshotBarrierContext } from './data-source-contracts.js';
export type { DatabaseRowShardWriteBarrier } from './data-source-contracts.js';
export type {
  DatabaseRowCellValueInsert,
  DatabaseRowSet,
} from './data-source-contracts.js';
export type {
  DatabaseMissingStorage,
  DatabaseRefusedChangeActions,
  DatabaseRefusedChangeReason,
  DatabaseRefusedChangeRequest,
  DatabaseRepairActions,
  DatabaseRepairOutcome,
  DatabaseRepairRequest,
  DatabaseRepairResult,
} from './data-source-contracts.js';
export { DatabasePublicationBaseUnavailable } from './data-source-contracts.js';
export type { ShardedDatabaseRootCreationInput } from './data-source-provisioning.js';
export type { ShardedDatabaseReferenceProps } from './data-source-provisioning.js';
export type { ShardedDatabaseHostBlockProvisionInput } from './data-source-provisioning.js';
export type { ShardedDatabaseHostBlockProvisionResult } from './data-source-provisioning.js';
export type { ShardedDatabaseBlockDataSourceProvisionInput } from './data-source-provisioning.js';
export type { ShardedDatabaseBlockDataSourceProvisionResult } from './data-source-provisioning.js';
export type { DatabaseCreationHosting } from './data-source-provisioning.js';
export { createShardedDatabaseReferenceForHostDoc } from './data-source-provisioning.js';
export { ensureShardedDatabaseRootForHostDoc } from './data-source-provisioning.js';
export {
  archivePageHosting,
  hostPageDocIds,
  livePageHosting,
  provisionShardedDatabaseHostBlock,
  publishDatabaseCreationDocuments,
  publishImportedDatabaseDocuments,
} from './data-source-provisioning.js';
export type { DatabaseRowSetGroupPlan } from './data-source-shard-writing.js';
export { resolveDatabaseStorageModel } from './data-source-storage.js';
export { isDatabaseStorageModelReady } from './data-source-storage.js';
export { observeDatabaseStorageModelReady } from './data-source-storage.js';
export { observeDatabaseSystemDocMissing } from './data-source-values.js';

/** A change needs every table part, but the worker could not read them all. */
export class DatabaseTablePartsUnavailableError extends Error {
  constructor() {
    super('Not every table part could be read');
    this.name = 'DatabaseTablePartsUnavailableError';
  }
}

/** The values of a text column are too large to become options. */
export class DatabaseTypeChangeValuesTooLargeError extends Error {
  constructor() {
    super('The column values are too large to become options');
    this.name = 'DatabaseTypeChangeValuesTooLargeError';
  }
}

export class DatabaseBlockDataSource extends DataSourceBase {
  private readonly runtime = this.createRuntime();

  private createRuntime(): DatabaseSourceRuntime {
    const source = this;
    return createDatabaseSourceRuntime({
      get propertyRegistry() {
        return DatabaseBlockDataSource.propertiesMap;
      },
      get dataSource() {
        return source;
      },
      get _model() {
        return getDatabaseRootReadModel(source.storageDoc) ?? source._model;
      },
      get _shardedRefreshFrame() {
        return source._shardedRefreshFrame;
      },
      set _shardedRefreshFrame(value) {
        source._shardedRefreshFrame = value;
      },
      get _shardedRowIds() {
        return source._shardedRowIds;
      },
      set _shardedRowIds(value) {
        source._shardedRowIds = value;
      },
      get _shardedRowsById() {
        return source._shardedRowsById;
      },
      set _shardedRowsById(value) {
        source._shardedRowsById = value;
      },
      get _shardedRowsRevision() {
        return source._shardedRowsRevision;
      },
      get isFileIndexDatabase() {
        return source.isFileIndexDatabase.bind(source);
      },
      get _rowShardEmptyConfirmedSnapshot() {
        return source._rowShardEmptyConfirmedSnapshot;
      },
      set _rowShardEmptyConfirmedSnapshot(value) {
        source._rowShardEmptyConfirmedSnapshot = value;
      },
      get databaseId() {
        return source.databaseId;
      },
      get storageDoc() {
        return source.storageDoc;
      },
      get _hostModel() {
        return source._hostModel;
      },
      get _disposed() {
        return source._disposed;
      },
      set _disposed(value) {
        source._disposed = value;
      },
      get readonly$() {
        return source.readonly$;
      },
      get _rowShardAutoRepairInFlight() {
        return source._rowShardAutoRepairInFlight;
      },
      set _rowShardAutoRepairInFlight(value) {
        source._rowShardAutoRepairInFlight = value;
      },
      get _rowShardEmptyConfirmationAttemptedSnapshot() {
        return source._rowShardEmptyConfirmationAttemptedSnapshot;
      },
      set _rowShardEmptyConfirmationAttemptedSnapshot(value) {
        source._rowShardEmptyConfirmationAttemptedSnapshot = value;
      },
      get _rowShardAutoRepairAbort() {
        return source._rowShardAutoRepairAbort;
      },
      set _rowShardAutoRepairAbort(value) {
        source._rowShardAutoRepairAbort = value;
      },
      get blockId() {
        return source.blockId;
      },
      get _rowShardAutoRepairAttemptedSnapshot() {
        return source._rowShardAutoRepairAttemptedSnapshot;
      },
      set _rowShardAutoRepairAttemptedSnapshot(value) {
        source._rowShardAutoRepairAttemptedSnapshot = value;
      },
      get _rowShardIntegrityValidationAbort() {
        return source._rowShardIntegrityValidationAbort;
      },
      set _rowShardIntegrityValidationAbort(value) {
        source._rowShardIntegrityValidationAbort = value;
      },
      get _rowShardIntegrityValidationAttemptedSnapshot() {
        return source._rowShardIntegrityValidationAttemptedSnapshot;
      },
      set _rowShardIntegrityValidationAttemptedSnapshot(value) {
        source._rowShardIntegrityValidationAttemptedSnapshot = value;
      },
      get _rowShardIntegrityValidationInFlight() {
        return source._rowShardIntegrityValidationInFlight;
      },
      set _rowShardIntegrityValidationInFlight(value) {
        source._rowShardIntegrityValidationInFlight = value;
      },
      get _rowShardAuthoritativeIntegritySnapshot() {
        return source._rowShardAuthoritativeIntegritySnapshot;
      },
      set _rowShardAuthoritativeIntegritySnapshot(value) {
        source._rowShardAuthoritativeIntegritySnapshot = value;
      },
      get mutateStorageRoot() {
        return source.mutateStorageRoot.bind(source);
      },
      get reportPublicationRejection() {
        return source.reportPublicationRejection.bind(source);
      },
      get captureStorageSync() {
        return source.captureStorageSync.bind(source);
      },
      get _rowShardIntegrityError() {
        return source._rowShardIntegrityError;
      },
      get _rowShardDocLoadDisposers() {
        return source._rowShardDocLoadDisposers;
      },
      get _missingRowShardDocIds() {
        return source._missingRowShardDocIds;
      },
      get _publicationViewStates() {
        return source._publicationViewStates;
      },
      get _publicationViewDisposers() {
        return source._publicationViewDisposers;
      },
      get _shardedReadyObservedDocs() {
        return source._shardedReadyObservedDocs;
      },
      get _shardedReadyDisposers() {
        return source._shardedReadyDisposers;
      },
      get _shardedObservedDocs() {
        return source._shardedObservedDocs;
      },
      get _shardedDocObserverDisposers() {
        return source._shardedDocObserverDisposers;
      },
      get _shardedWindowOwners() {
        return source._shardedWindowOwners;
      },
      get _pinnedShardedDocs() {
        return source._pinnedShardedDocs;
      },
      get _activeShardedWindowDocs() {
        return source._activeShardedWindowDocs;
      },
      set _activeShardedWindowDocs(value) {
        source._activeShardedWindowDocs = value;
      },
      get _shardedRowsByDocId() {
        return source._shardedRowsByDocId;
      },
      get _shardedSearchTextByRowId() {
        return source._shardedSearchTextByRowId;
      },
      get isSpacialProperty() {
        return source.isSpacialProperty.bind(source);
      },
      get _shardedSearchPropertySignature() {
        return source._shardedSearchPropertySignature;
      },
      set _shardedSearchPropertySignature(value) {
        source._shardedSearchPropertySignature = value;
      },
      get assertFileIndexRootSchemaWithinLimits() {
        return source.assertFileIndexRootSchemaWithinLimits.bind(source);
      },
      get _fileIndexShardRegistryDisposer() {
        return source._fileIndexShardRegistryDisposer;
      },
      set _fileIndexShardRegistryDisposer(value) {
        source._fileIndexShardRegistryDisposer = value;
      },
      get _fileIndexWriterShardId() {
        return source._fileIndexWriterShardId;
      },
      set _fileIndexWriterShardId(value) {
        source._fileIndexWriterShardId = value;
      },
      get assertFileIndexCellMutationAllowed() {
        return source.assertFileIndexCellMutationAllowed.bind(source);
      },
      get assertFileIndexRowMutationAllowed() {
        return source.assertFileIndexRowMutationAllowed.bind(source);
      },
      get doc() {
        return source.doc;
      },
      get rowCount$() {
        return source.rowCount$;
      },
      get _reactiveShardedCellValues() {
        return source._reactiveShardedCellValues;
      },
      get _reactiveShardedCellFrames() {
        return source._reactiveShardedCellFrames;
      },
      get _batch() {
        return source._batch;
      },
      set _batch(value) {
        source._batch = value;
      },
      get getBoundedPropertyIdsWindow() {
        return source.getBoundedPropertyIdsWindow.bind(source);
      },
      get columnCount$() {
        return source.columnCount$;
      },
      get sourceRowIds$() {
        return source.sourceRowIds$;
      },
      get spacialValueGet() {
        return source.spacialValueGet.bind(source);
      },
      get isFixedProperty() {
        return source.isFixedProperty.bind(source);
      },
      get workspaceId() {
        return source.workspaceId;
      },
      get docId() {
        return source.docId;
      },
      get viewDataList$() {
        return source.viewDataList$;
      },
      get pruneLocalUiStateForDeletedView() {
        return source.pruneLocalUiStateForDeletedView.bind(source);
      },
    });
  }

  private readonly localUi = new DatabaseLocalUiController({
    rootDocId: () => this._model.store.id,
    databaseId: () => this._databaseIdentity.databaseId,
    viewManager: () => this.databaseViewManager,
    rowIdsForLayout: () => this.getTableLayoutRowIdsForFiltering(),
  });

  override get parentProvider() {
    return this._hostModel.store.provider;
  }

  // Streamlain: Expose database block ID for bulk upload and other features
  get blockId(): string {
    return this._hostModel.id;
  }

  get databaseId(): string {
    return this._databaseIdentity.databaseId;
  }

  /** Row-shard docs the server confirmed it does not hold. */
  get missingRowShardDocIds(): readonly string[] {
    return [...this._missingRowShardDocIds];
  }

  get docId(): string {
    return this._hostModel.store.id;
  }

  get workspaceId(): string {
    return this._hostModel.store.workspace.id;
  }

  private isFileIndexDatabase(): boolean {
    return this._model.props.managedBy === FILE_INDEX_MANAGED_BY;
  }

  private assertFileIndexRootSchemaWithinLimits(
    overrides: {
      columns?: unknown;
      manifest?: unknown;
      registryShards?: readonly DatabaseShardManifestEntry[];
      views?: unknown;
    },
    model: DatabaseBlockModel = this._model
  ): void {
    if (model.props.managedBy !== FILE_INDEX_MANAGED_BY) return;
    const views = overrides.views ?? model.props.views;
    if (Array.isArray(views) && views.length > FILE_INDEX_VIEW_HARD_LIMIT) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Files database supports at most ${FILE_INDEX_VIEW_HARD_LIMIT} views.`
      );
    }
    const registryShards =
      overrides.registryShards ??
      readFileIndexShardEntries(
        getFileIndexShardRegistry(model.store.doc.spaceDoc, this.databaseId)
      );
    const encodedBytes = new TextEncoder().encode(
      JSON.stringify({
        columns: overrides.columns ?? model.props.columns,
        databaseShardManifest:
          overrides.manifest ?? storedDatabaseShardDirectory(model.props),
        shardRegistry: registryShards,
        views,
      })
    ).byteLength;
    if (encodedBytes > FILE_INDEX_ROOT_SCHEMA_HARD_LIMIT_BYTES) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Files database root schema exceeds ${FILE_INDEX_ROOT_SCHEMA_HARD_LIMIT_BYTES} bytes.`
      );
    }
  }

  private fileIndexRowsManagedReason(): string {
    return 'Rows in the Files database are created from canvas files.';
  }

  override rowCanAdd(): boolean {
    return this.rowAddDisabledReason() === undefined;
  }

  override rowAddDisabledReason(): string | undefined {
    if (this.isFileIndexDatabase()) return this.fileIndexRowsManagedReason();
    const rowCount = this.rowCount$.value;
    const limit = DATABASE_PRODUCT_LIMITS.rowHardLimit;
    return rowCount >= limit
      ? databaseLimitNoticeText({
          kind: 'rows',
          limit,
          actual: rowCount,
          unit: 'rows',
        })
      : undefined;
  }

  private isFileIndexSystemProperty(propertyId: string): boolean {
    if (!this.isFileIndexDatabase()) return false;
    return (
      isFileIndexSystemColumnId(propertyId) ||
      isFileIndexManagedColumnData(this.propertyDataGet(propertyId))
    );
  }

  private assertFileIndexRowMutationAllowed(
    actor: DataSourceWriteActor,
    operation: string
  ): void {
    if (!this.isFileIndexDatabase() || actor === 'system') return;
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `${operation} is not allowed: ${this.fileIndexRowsManagedReason()}`
    );
  }

  private assertFileIndexCellMutationAllowed(
    propertyId: string,
    actor: DataSourceWriteActor
  ): void {
    if (!this.isFileIndexSystemProperty(propertyId) || actor === 'system') {
      return;
    }
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Column "${propertyId}" is managed by the Files database. Create a user column for manual or automation writes.`
    );
  }

  // Rows are no block models: a spacial value lives in the row's shard
  // cell, so a row without that cell has none.
  spacialProperties: Record<string, SpacialProperty> = {
    'created-time': { valueSet: () => {}, valueGet: () => null },
    'created-by': { valueSet: () => {}, valueGet: () => null },
    'updated-time': { valueSet: () => {}, valueGet: () => null },
    'updated-by': { valueSet: () => {}, valueGet: () => null },
    type: { valueSet: () => {}, valueGet: () => undefined },
    title: { valueSet: () => {}, valueGet: () => undefined },
  };

  isSpacialProperty(propertyType: string): boolean {
    return this.spacialProperties[propertyType] !== undefined;
  }

  spacialValueGet(
    rowId: string,
    propertyId: string,
    propertyType: string
  ): unknown {
    return this.spacialProperties[propertyType]?.valueGet(rowId, propertyId);
  }

  static externalProperties = signal<PropertyMetaConfig[]>([]);
  static propertiesList = computed(() => {
    return [
      ...Object.values(databaseBlockProperties),
      ...this.externalProperties.value,
    ];
  });
  static propertiesMap = computed(() => {
    return Object.fromEntries(
      this.propertiesList.value.map(v => [v.type, v as PropertyMetaConfig])
    );
  });

  static registerWriterPorts(
    workspace: object,
    ports: DatabaseWriterPorts
  ): () => void {
    return registerDatabaseWriterPorts(workspace, ports);
  }

  /**
   * Row sets of several sharded databases planned as one publication; the
   * caller checks the document count and commits once.
   */
  static planRowSetGroup(
    entries: ReadonlyArray<{
      readonly source: DatabaseBlockDataSource;
      readonly set: DatabaseRowSet;
    }>,
    actor: DataSourceWriteActor = 'user'
  ): DatabaseRowSetGroupPlan {
    return planRowSetGroup(
      entries.map(({ source, set }) => ({ source: source.runtime, set })),
      actor
    );
  }

  commitRowMutation(
    input: DatabaseRowMutation,
    signal?: AbortSignal
  ): Promise<LocalPublicationReceipt | null> {
    // The caller awaits its commit and receives the rejection itself.
    return claimRowPublication(
      this.runtime,
      this.runtime.commitRowMutation(input, signal)
    );
  }

  applyImportedCommits(commits: readonly DatabaseImportCommit[]): void {
    applyImportedCommits.call(this.runtime, commits);
  }

  applyArchivedImport(
    importer: DatabaseImportPort,
    jobId: string,
    commits: readonly DatabaseImportCommitSummary[]
  ): Promise<void> {
    return applyArchivedImport.call(this.runtime, importer, jobId, commits);
  }

  get importWriterId(): string {
    return currentWriterId.call(this.runtime);
  }

  /** Relation source and steal edits enter one canonical mutation together. */
  commitCellValues(
    edits: readonly { rowId: string; propertyId: string; value: unknown }[],
    signal?: AbortSignal
  ): Promise<LocalPublicationReceipt | null> {
    assertNoAtomicRowMutation(this.runtime);
    if (signal?.aborted)
      return Promise.reject(new Error('Database mutation was cancelled.'));
    const prepared = new Map<
      string,
      import('./data-source-contracts.js').RowShardCells
    >();
    for (const edit of edits) {
      const cell = this.runtime.resolveShardedCellForWrite(
        edit.rowId,
        edit.propertyId,
        edit.value,
        'user'
      );
      if (!cell)
        throw new BlockSuiteError(
          ErrorCode.DatabaseBlockError,
          'The column value cannot be written.'
        );
      let cells = prepared.get(edit.rowId);
      if (!cells) prepared.set(edit.rowId, (cells = {}));
      cells[edit.propertyId] = cell;
    }
    return writePreparedShardedCellsToLocalDocs.call(this.runtime, prepared);
  }

  static registerBulkProvider(
    store: object,
    port: DatabaseClientBulkPort
  ): () => void {
    databaseBulkProviders.set(store, port);
    return () => {
      if (databaseBulkProviders.get(store) === port)
        databaseBulkProviders.delete(store);
    };
  }

  static registerProjectionProvider(
    store: object,
    provider: DatabaseProjectionProvider
  ): () => void {
    const providers =
      databaseProjectionProviders.get(store) ??
      new Set<DatabaseProjectionProvider>();
    providers.add(provider);
    databaseProjectionProviders.set(store, providers);
    return () => {
      providers.delete(provider);
      if (!providers.size) databaseProjectionProviders.delete(store);
    };
  }

  static registerSystemDocMissingObserver(
    store: object,
    observer: DatabaseSystemDocMissingObserver
  ): () => void {
    return registerDatabaseSystemDocMissingObserver(store, observer);
  }

  static registerPublicationViewObserver(
    store: object,
    observer: DatabasePublicationViewObserver
  ): () => void {
    return registerDatabasePublicationViewObserver(store, observer);
  }

  static registerRowShardWriteBarrier(
    store: object,
    barrier: DatabaseRowShardWriteBarrier
  ): () => void {
    return registerRowShardBarrier(rowShardWriteBarriers, store, barrier);
  }

  private _batch = 0;

  private _disposed = false;

  private _fileIndexShardRegistryDisposer?: () => void;

  private _fileIndexWriterShardId?: string;

  private readonly _hostModel: DatabaseBlockModel;

  private readonly _model: DatabaseBlockModel;

  private readonly _databaseIdentity: ResolvedDatabaseIdentity;

  override featureFlags$: ReadonlySignal<DatabaseFlags> = computed(() => {
    const featureFlagService = this._hostModel.store.get(FeatureFlagService);
    const enableNumberFormat = featureFlagService.getFlag(
      'enable_database_number_formatting'
    );
    const enableTableVirtualScroll = featureFlagService.getFlag(
      'enable_table_virtual_scroll'
    );
    return {
      enable_number_formatting: enableNumberFormat ?? false,
      enable_table_virtual_scroll: enableTableVirtualScroll ?? false,
      use_shared_canvas_viewport: false,
    };
  });

  protected override sourcePropertyIds$: ReadonlySignal<string[]> = computed(
    () => {
      const fixedPropertiesSet = new Set(this.fixedProperties$.value);
      const properties: string[] = [];
      this._model.props.columns$.value.forEach(column => {
        if (fixedPropertiesSet.has(column.type)) {
          fixedPropertiesSet.delete(column.type);
        }
        properties.push(column.id);
      });

      // Virtual system fields must not consume the bounded table window before
      // persisted user columns. Keep the virtual title first when it has no
      // stored column; hidden metadata remains available in later windows.
      const fixedProperties = [...fixedPropertiesSet];
      const result = [
        ...fixedProperties.filter(type => type === 'title'),
        ...properties,
        ...fixedProperties.filter(type => type !== 'title'),
      ];
      return result;
    }
  );

  readonly$: ReadonlySignal<boolean> = computed(() => {
    return this._hostModel.store.readonly;
  });

  /** sl-9qm.21.3: true when database is system-managed (no manual edits) */
  override systemManaged$: ReadonlySignal<boolean> = computed(() => {
    return this._model.props.systemManaged === true;
  });

  /** sl-9qm.21.3: true when system-managed DB allows row deletion (moderation) */
  override allowDelete$: ReadonlySignal<boolean> = computed(() => {
    return this._model.props.allowDelete === true;
  });

  /** sl-web-9n8: true when column structure is locked (rows still editable) */
  schemaLocked$: ReadonlySignal<boolean> = computed(() => {
    return this._model.props.schemaLocked === true;
  });

  private _shardedRefreshFrame: ReturnType<
    typeof requestAnimationFrame
  > | null = null;

  private readonly _shardedObservedDocs = new Set<string>();

  private _activeShardedWindowDocs = new Set<string>();
  private readonly _shardedWindowOwners = new Map<string, Set<string>>();
  private readonly _pinnedShardedDocs = new Set<string>();
  private _projectionController?: DatabaseProjectionController;
  private _projectionSubscriberId = 0;
  private readonly _plainProjectionOwners = new Map<
    string,
    string | undefined
  >();

  private readonly _shardedReadyObservedDocs = new Set<string>();

  private readonly _shardedDocObserverDisposers = new Map<string, () => void>();

  private readonly _shardedReadyDisposers = new Map<string, () => void>();

  private readonly _rowShardDocLoadDisposers = new Map<
    string,
    Array<() => void>
  >();

  private readonly _shardedRowsRevision = signal(0);

  private readonly _missingRowShardDocIds = new Set<string>();

  private readonly _publicationViewStates = new Map<
    string,
    DatabasePublicationViewState
  >();

  private readonly _publicationViewDisposers = new Map<string, () => void>();

  private readonly _rowShardIntegrityError = signal<string | null>(null);

  private _rowShardAutoRepairAbort?: AbortController;

  private _rowShardAutoRepairAttemptedSnapshot?: string;

  private _rowShardAutoRepairInFlight?: Promise<void>;

  private _rowShardIntegrityValidationAbort?: AbortController;

  private _rowShardIntegrityValidationAttemptedSnapshot?: string;

  private _rowShardIntegrityValidationInFlight?: Promise<void>;

  private _rowShardAuthoritativeIntegritySnapshot?: string;

  private _rowShardEmptyConfirmationAttemptedSnapshot?: string;

  private _rowShardEmptyConfirmedSnapshot?: string;

  private _shardedRowIds: string[] = [];

  private _shardedRowsById = new Map<string, ShardedRowRecord>();

  private readonly _shardedRowsByDocId = new Map<
    string,
    ShardedRowsDocCacheEntry
  >();

  private readonly _shardedSearchTextByRowId = new Map<string, string>();

  private _shardedSearchPropertySignature = '';

  private readonly _reactiveShardedCellValues = new Map<
    string,
    ReactiveShardedCellValue
  >();

  private readonly _reactiveShardedCellFrames = new Map<
    string,
    ReturnType<typeof requestAnimationFrame>
  >();

  protected override sourceRowIds$: ReadonlySignal<string[]> = computed(() => {
    this._shardedRowsRevision.value;
    return this._shardedRowIds.slice();
  });

  override rowCount$: ReadonlySignal<number> = computed(() => {
    // Root updates invalidate the count before synchronous root/shard consumers
    // read their window; the row-cache refresh follows the group application.
    // Track top-level replacement, but read the current nested CRDT manifest.
    // The shallow signal retains the pre-split object after nested updates.
    trackDatabaseShardDirectory(this._model);
    this._shardedRowsRevision.value;
    return shownShardDirectoryRowCount(this.runtime);
  });

  /**
   * The complete row-shard directory: ready with every shard entry, loading
   * while directory parts are on their way, or missing once the server
   * confirmed a part absent. Throws for a database without a directory head.
   */
  getCompleteShardDirectory(): DatabaseShardDirectoryCompleteRead {
    return this.runtime.getCompleteShardDirectory();
  }

  /** Calls `onChange` after loaded shard rows or directory parts change. */
  onShardedRowsChange(onChange: () => void): () => void {
    let initial = true;
    return this._shardedRowsRevision.subscribe(() => {
      if (initial) {
        initial = false;
        return;
      }
      onChange();
    });
  }

  ensureRelationRowDataLoaded(): void {
    const directory = this.runtime.getCompleteShardDirectory();
    if (directory.status !== 'ready') return;
    for (const shard of directory.manifest.shards) {
      this.ensureShardObserved(shard, 'relation');
    }
  }

  /** Missing once the server confirmed a directory part of the rows absent. */
  getRelationRowDataLoadState(): 'loading' | 'ready' | 'missing' {
    this._shardedRowsRevision.value;
    const directory = this.runtime.getCompleteShardDirectory();
    if (directory.status !== 'ready') return directory.status;
    const manifest = directory.manifest;

    this.refreshShardedRowsFromDocs(manifest.shards);

    return this.areAllManifestShardRowsMaterialized(manifest)
      ? 'ready'
      : 'loading';
  }

  /**
   * Resolve a search result against its authoritative row-shard snapshot
   * without materializing every shard in the database.
   *
   * The row id remains the stable semantic identity. The shard document id is
   * only a current locator and is validated against the loaded shard before an
   * absolute position is returned.
   */
  resolveRowShardAbsoluteIndex(
    rowId: string,
    rowShardDocId: string
  ): number | null {
    const directory = this.runtime.getLoadedShardDirectory(0);

    // The position counts from the row its directory part starts at; a hit
    // in a part not loaded yet waits for the parts, which it requests.
    const shard = directory.loadedManifest.shards.find(
      entry => entry.serverDocId === rowShardDocId
    );
    if (!shard && directory.missingParts.length) {
      this.runtime.getCompleteShardDirectory();
      return null;
    }
    if (!shard) {
      throw new Error(
        `Database row shard locator is stale: database=${this._model.id} row=${rowId} shard=${rowShardDocId}`
      );
    }
    const shardRowStart = databaseShardDirectoryShardRowStart(
      directory,
      entry => entry.serverDocId === rowShardDocId
    )!;
    const shardDoc = this.ensureShardObserved(shard, 'row-window');
    if (!shardDoc.ready || !this.hasCompleteLoadedShardRows(shard, shardDoc)) {
      return null;
    }

    const localIndex = shardDoc.spaceDoc
      .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
      .toArray()
      .map(value => String(value))
      .indexOf(rowId);
    if (localIndex < 0) {
      throw new Error(
        `Database row is absent from its indexed shard: database=${this._model.id} row=${rowId} shard=${rowShardDocId}`
      );
    }

    return shardRowStart + localIndex;
  }

  viewConverts = databaseBlockViewConverts;

  viewDataList$: ReadonlySignal<DataViewDataType[]> = computed(() => {
    return getOrderedViews(
      this._model.props.views$.value as DataViewDataType[]
    ).map(getViewDataForRead);
  });

  private readonly databaseViewManager: DatabaseBlockViewManager;

  override viewManager: ViewManager;

  get viewMetas() {
    return getCreatableDatabaseBlockViews();
  }

  get doc() {
    return this._hostModel.store;
  }

  get storageModel() {
    return this._model;
  }

  private get storageDoc() {
    return this._model.store;
  }

  private mutateStorageRoot(
    mutate: NonNullable<DatabaseRowMutation['rootMutation']>,
    kind: DatabaseRowMutation['kind'] = 'schema',
    interactive = true,
    onSettled?: () => void
  ): void {
    queueDatabaseRootMutation(
      this.runtime,
      mutate,
      kind,
      interactive,
      onSettled
    );
  }

  titleSet(text: string): void {
    this.mutateStorageRoot(stage => {
      stage.props.title.replace(0, stage.props.title.length, text);
    });
  }

  canvasPresentationSet(state: CanvasDatabasePresentationState): void {
    this.captureStorageSync();
    this.mutateStorageRoot(stage => {
      stage.props.canvasPresentation = state;
    });
  }

  activeViewIdSet(id: string | undefined): void {
    this.captureStorageSync();
    this.mutateStorageRoot(stage => {
      if (id && !stage.props.views.some(view => view.id === id))
        throw new PublicationError('LOCAL_CONFLICT');
      stage.props.activeViewId = id;
    });
  }

  private captureStorageSync(): void {
    this.storageDoc.captureSync();
  }

  allPropertyMetas$ = computed<PropertyMetaConfig<any, any, any, any>[]>(() => {
    return DatabaseBlockDataSource.propertiesList.value;
  });

  propertyMetas$ = computed<PropertyMetaConfig[]>(() => {
    return this.allPropertyMetas$.value.filter(
      v => !v.config.fixed && !v.config.hide
    );
  });

  constructor(
    model: DatabaseBlockModel,
    init?: (dataSource: DatabaseBlockDataSource) => void
  ) {
    super();
    this._hostModel = model;
    assertDatabaseIdentity(model);
    this._model = resolveDatabaseStorageModel(model);
    this._databaseIdentity = assertDatabaseIdentity(this._model);
    this.databaseViewManager = new DatabaseBlockViewManager(this, this._model);
    this.viewManager = this.databaseViewManager;
    this.initializeFileIndexShardRegistry();
    this._releaseRootAgent = observeDatabaseRootAgent(model);
    init?.(this); // then allow external initialisation
  }

  private getProjectionController(): DatabaseProjectionController | undefined {
    const provider = (
      databaseProjectionProviders.get(this._hostModel.store) ??
      databaseProjectionProviders.get(this._hostModel.store.workspace)
    )
      ?.values()
      .next().value;
    if (!provider) return undefined;
    if (!this._projectionController)
      this._projectionController = new DatabaseProjectionController({
        provider,
        rootDocId: this._model.store.id,
        databaseId: this.databaseId,
        relevant: docId => {
          if (docId === this._model.store.id) return true;
          const directory = requireLoadedShardDirectory(
            this._model,
            this.databaseId
          );
          if (directory.manifest.shards.some(s => s.serverDocId === docId))
            return true;
          // Directory parts change the shard list; until all are loaded any
          // document of this database may hold projected rows.
          const parsed = classifyPageSubdocId(
            docId,
            this._model.store.workspace.id
          );
          return (
            parsed !== null &&
            parsed.kind !== 'page_part' &&
            parsed.databaseId === this.databaseId &&
            (parsed.kind === 'database-directory-part' ||
              directory.missingParts.length > 0)
          );
        },
        hydrate: (owner, rowIds) => {
          const directory = this.runtime.getCompleteShardDirectory();
          if (directory.status !== 'ready') return false;
          const manifest = directory.manifest;
          const requested = new Set(rowIds);
          const named = new Set(
            selectDatabaseShardsMayContainRows(manifest.shards, rowIds)
          );
          const shards = manifest.shards.filter(
            shard =>
              named.has(shard) ||
              this._shardedRowsByDocId
                .get(shard.serverDocId)
                ?.rows.some(row => requested.has(row.id))
          );
          setWindowShardDocuments(
            this.runtime,
            owner,
            new Set(shards.flatMap(shard => this.runtime.getShardDocIds(shard)))
          );
          for (const shard of shards) {
            this.runtime.ensureShardObserved(shard);
            for (const row of this.runtime.readLoadedShardRows(shard))
              this._shardedRowsById.set(row.id, row);
          }
          return rowIds.every(id => this._shardedRowsById.has(id));
        },
        release: owner => releaseWindowShardDocuments(this.runtime, owner),
      });
    return this._projectionController;
  }

  private isPlainProjection(query: DatabaseProjectionQuery): boolean {
    const groups = query.groupBy;
    const hasGroups = Array.isArray(groups) ? groups.length > 0 : !!groups;
    return !!query.window && !hasGroups && canProjectSourceWindow(query, false);
  }

  private projectPlainWindow(
    query: DatabaseProjectionQuery,
    owner = query.viewId
  ): DatabaseProjectionSubscriptionResult {
    const window = this.getRowsWindow({ ...query.window!, owner });
    const complete =
      !this._rowShardIntegrityError.value &&
      window.rowIds.length ===
        Math.min(window.limit, Math.max(0, window.totalCount - window.offset));
    return {
      query,
      aggregates: [],
      groups: [],
      complete,
      diagnostics: [],
      freshness: complete ? 'live' : 'stale',
      syncState: complete ? 'live' : 'partial',
      source: 'local-window',
      rowIds: window.rowIds,
      totalCount: window.totalCount,
      totalKnown: true,
      window: { offset: window.offset, limit: window.limit },
    };
  }

  private bindProjectionWindow(
    query: DatabaseProjectionQuery
  ): DatabaseProjectionQuery {
    if (query.window || query.scope === 'whole-database') return query;
    const view = query.viewId
      ? this.viewManager.viewGet(query.viewId)
      : undefined;
    return {
      ...query,
      window: {
        offset: view?.rowWindowOffset$.value ?? 0,
        limit: view?.rowWindowLimit$.value ?? INITIAL_DATABASE_ROW_WINDOW_LIMIT,
      },
    };
  }

  override projectRows(query: DatabaseProjectionQuery) {
    query = this.bindProjectionWindow(query);
    this._shardedRowsRevision.value;
    if (this.isPlainProjection(query)) {
      const owner = databaseProjectionConsumerKey('plain-view', query);
      this._plainProjectionOwners.delete(owner);
      this._plainProjectionOwners.set(owner, query.viewId);
      // Retain the new window before releasing the previous execution path.
      const result = this.projectPlainWindow(query, owner);
      if (query.viewId)
        this._projectionController?.releaseViewRows(query.viewId, query);
      while (this._plainProjectionOwners.size > 32) {
        const oldest = this._plainProjectionOwners.keys().next().value;
        if (oldest === undefined) break;
        releaseWindowShardDocuments(this.runtime, oldest);
        this._plainProjectionOwners.delete(oldest);
      }
      return result;
    }
    const controller = this.getProjectionController();
    if (query.viewId && controller) {
      const owner = databaseProjectionConsumerKey('plain-view', query);
      releaseWindowShardDocuments(this.runtime, owner);
      this._plainProjectionOwners.delete(owner);
      controller.setViewRevision(
        query.viewId,
        projectionCacheKey(
          'groups',
          databaseProjectionViewState(this.viewDataList$.value, query.viewId)
        )
      );
    }
    return (
      controller?.get(query) ??
      emptyDatabaseProjection(
        query,
        false,
        'The database projection provider is unavailable.'
      )
    );
  }

  override subscribeProjection(
    query: DatabaseProjectionQuery,
    subscriber: (result: DatabaseProjectionSubscriptionResult) => void
  ): () => void {
    query = this.bindProjectionWindow(query);
    if (this.isPlainProjection(query)) {
      const owner = `window-subscriber:${++this._projectionSubscriberId}`;
      const stop = effect(() =>
        subscriber(this.projectPlainWindow(query, owner))
      );
      return () => {
        stop();
        releaseWindowShardDocuments(this.runtime, owner);
      };
    }
    const controller = this.getProjectionController();
    if (!controller) {
      subscriber(
        emptyDatabaseProjection(
          query,
          false,
          'The database projection provider is unavailable.'
        )
      );
      return () => {};
    }
    return controller.subscribe(query, subscriber, () => {
      this._shardedRowsRevision.value;
    });
  }

  override atomicChange(fn: () => void): void {
    atomicDatabaseRootChange(this.runtime, fn);
  }
  override openPublicationScope(): DatabasePublicationScope {
    return openPublicationScope(this.runtime);
  }
  override async flushLocalWrites(): Promise<void> {
    await flushLocalRowPublications(this.runtime);
  }
  override propertyDataUpdate(
    propertyId: string,
    updater: (data: Record<string, unknown>) => Record<string, unknown>
  ): void {
    this.withLimitNotice('user', () =>
      this.runtime.updateProperty(propertyId, column =>
        this.runtime._model.props.schemaLocked ||
        this.runtime.propertyReadonlyGet(propertyId)
          ? {}
          : { data: updater(column.data) }
      )
    );
  }

  /** Wait for the canonical local-first row/root ACK before the next producer chunk. */
  async flushRowWrites(signal?: AbortSignal): Promise<void> {
    await flushLocalRowPublications(this.runtime);
    await flushRowShardWrites(this.runtime, signal);
  }

  /** Releases the root document from the agent pointers of the page. */
  private readonly _releaseRootAgent: () => void;

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this._releaseRootAgent();
    disposeRowShardWrites(this.runtime);
    disposeDirectoryCompleteWait(this.runtime);
    releaseRowWriterSessions(this.runtime);
    this._bulkAbort?.abort();
    this._bulkReceiptOwner.dispose();
    this._projectionController?.dispose();
    this._projectionController = undefined;
    this._plainProjectionOwners.clear();
    this._shardedWindowOwners.clear();
    this._pinnedShardedDocs.clear();

    this._rowShardAutoRepairAbort?.abort();
    this._rowShardAutoRepairAbort = undefined;
    this._rowShardAutoRepairInFlight = undefined;
    this._rowShardIntegrityValidationAbort?.abort();
    this._rowShardIntegrityValidationAbort = undefined;
    this._rowShardIntegrityValidationInFlight = undefined;
    this._rowShardEmptyConfirmationAttemptedSnapshot = undefined;
    this._rowShardEmptyConfirmedSnapshot = undefined;

    this._fileIndexShardRegistryDisposer?.();
    this._fileIndexShardRegistryDisposer = undefined;

    for (const dispose of this._shardedReadyDisposers.values()) {
      dispose();
    }
    this._shardedReadyDisposers.clear();
    for (const dispose of this._publicationViewDisposers.values()) {
      dispose();
    }
    this._publicationViewDisposers.clear();
    this._publicationViewStates.clear();
    this._shardedReadyObservedDocs.clear();

    for (const dispose of this._shardedDocObserverDisposers.values()) {
      dispose();
    }
    this._shardedDocObserverDisposers.clear();
    this._shardedObservedDocs.clear();
    this._activeShardedWindowDocs.clear();
    this._shardedRowsByDocId.clear();

    for (const disposers of this._rowShardDocLoadDisposers.values()) {
      for (const dispose of disposers) {
        dispose();
      }
    }
    this._rowShardDocLoadDisposers.clear();

    if (this._shardedRefreshFrame !== null) {
      cancelAnimationFrame(this._shardedRefreshFrame);
      this._shardedRefreshFrame = null;
    }

    for (const frame of this._reactiveShardedCellFrames.values()) {
      cancelAnimationFrame(frame);
    }
    this._reactiveShardedCellFrames.clear();

    for (const entry of this._reactiveShardedCellValues.values()) {
      entry.dispose();
    }
    this._reactiveShardedCellValues.clear();

    if (this._batch) {
      cancelAnimationFrame(this._batch);
      this._batch = 0;
    }
  }

  setUiStatePersistence(mode: DatabaseUiStatePersistence): void {
    return this.localUi.setUiStatePersistence(mode);
  }

  persistLocalCurrentView(viewId: string | undefined): void {
    return this.localUi.persistLocalCurrentView(viewId);
  }

  setTransientCurrentView(id: string | undefined): void {
    return this.localUi.setTransientCurrentView(id);
  }

  override getViewWindowState(
    viewId: string
  ): DataSourceViewWindowState | undefined {
    return this.localUi.getViewWindowState(viewId);
  }

  override setViewWindowState(
    viewId: string,
    patch: DataSourceViewWindowState
  ): boolean {
    return this.localUi.setViewWindowState(viewId, patch);
  }

  override getViewLayoutState(
    viewId: string,
    viewMode: string
  ): DataSourceViewLayoutState | undefined {
    return this.localUi.getViewLayoutState(viewId, viewMode);
  }

  override setViewLayoutState(
    viewId: string,
    state: DataSourceViewLayoutState
  ): boolean {
    return this.localUi.setViewLayoutState(viewId, state);
  }

  override getTableViewLayoutState(
    viewId: string,
    columnLayoutSignature: string
  ): DataSourceTableViewLayoutState | undefined {
    return this.localUi.getTableViewLayoutState(viewId, columnLayoutSignature);
  }

  override setTableViewLayoutState(
    viewId: string,
    state: DataSourceTableViewLayoutState
  ): boolean {
    return this.localUi.setTableViewLayoutState(viewId, state);
  }

  private getTableLayoutRowIdsForFiltering(): Set<string> | undefined {
    this._shardedRowsRevision.value;
    const directory = this.runtime.getCompleteShardDirectory();
    if (directory.status !== 'ready') return undefined;

    const expectedRows = this.getManifestRowCount(directory.manifest);
    if (
      expectedRows > 0 &&
      (this._shardedRowIds.length !== expectedRows ||
        this._shardedRowsById.size !== expectedRows)
    ) {
      return undefined;
    }

    return new Set(this._shardedRowIds);
  }

  private pruneLocalUiStateForDeletedView(
    viewId: string,
    nextViewId: string | undefined
  ): void {
    this._projectionController?.releaseView(viewId);
    for (const [owner, ownerViewId] of this._plainProjectionOwners) {
      if (ownerViewId !== viewId) continue;
      releaseWindowShardDocuments(this.runtime, owner);
      this._plainProjectionOwners.delete(owner);
    }
    releaseWindowShardDocuments(this.runtime, viewId);
    return this.localUi.pruneLocalUiStateForDeletedView(viewId, nextViewId);
  }

  private getManifestRowCount(
    manifest: ReturnType<typeof normalizeDatabaseShardManifest>
  ): number {
    return this.runtime.getManifestRowCount(manifest);
  }

  getInitialRowWindowReadiness(
    limit: number = INITIAL_DATABASE_ROW_WINDOW_LIMIT
  ): DatabaseRowShardReadiness {
    return this.runtime.getInitialRowWindowReadiness(limit);
  }

  getContentReadiness(): DatabaseRowShardReadiness {
    return this.runtime.getContentReadiness();
  }

  isInitialRowWindowReady(
    limit: number = INITIAL_DATABASE_ROW_WINDOW_LIMIT
  ): boolean {
    return this.runtime.isInitialRowWindowReady(limit);
  }

  repairLoadedRowShardManifestDrift(
    expectedSnapshot?: string
  ): Promise<DatabaseRowShardRepairResult> {
    return this.runtime.repairLoadedRowShardManifestDrift(expectedSnapshot);
  }

  private areAllManifestShardRowsMaterialized(
    manifest: ReturnType<typeof normalizeDatabaseShardManifest>
  ): boolean {
    return this.runtime.areAllManifestShardRowsMaterialized(manifest);
  }

  private hasCompleteLoadedShardRows(
    shard: DatabaseShardManifestEntry,
    doc: DatabaseShardDocument | null | undefined
  ): boolean {
    return this.runtime.hasCompleteLoadedShardRows(shard, doc);
  }

  private ensureShardObserved(
    shard: DatabaseShardManifestEntry,
    reason: DatabaseRowShardDocLoadReason = 'row-window'
  ): DatabaseShardDocument {
    return this.runtime.ensureShardObserved(shard, reason);
  }

  private refreshShardedRowsFromDocs(
    shards?: readonly DatabaseShardManifestEntry[]
  ): void {
    return this.runtime.refreshShardedRowsFromDocs(shards);
  }

  private initializeFileIndexShardRegistry(): void {
    return this.runtime.initializeFileIndexShardRegistry();
  }

  provisionInitialShard(): void {
    return this.runtime.provisionInitialShard();
  }

  rowAddWithId(
    rowId: string,
    insertPosition: InsertToPosition | number,
    actor: DataSourceWriteActor = 'user'
  ): { rowId: string; created: boolean } {
    assertNoAtomicRowMutation(this.runtime);
    return this.runtime.rowAddWithId(rowId, insertPosition, actor);
  }

  rowAddManyWithCellValues(
    rows: readonly DatabaseRowCellValueInsert[],
    actor: DataSourceWriteActor = 'user'
  ): number {
    assertNoAtomicRowMutation(this.runtime);
    return this.runtime.rowAddManyWithCellValues(rows, actor);
  }

  /**
   * Creates or changes `set.upserts` and deletes `set.deleteRowIds` in one
   * local publication with one receipt; `null` for an empty set.
   */
  commitRowSet(
    set: DatabaseRowSet,
    signal?: AbortSignal,
    actor: DataSourceWriteActor = 'user'
  ): Promise<LocalPublicationReceipt | null> {
    assertNoAtomicRowMutation(this.runtime);
    return this.runtime.commitRowSet(set, signal, actor);
  }

  override getRowsWindow(request: {
    offset?: number;
    limit: number;
    owner?: string;
  }): DataSourceRowsWindow {
    return this.runtime.getRowsWindow(request);
  }

  override getProjectionSource(
    query: DatabaseProjectionQuery
  ): DatabaseProjectionSource {
    return this.runtime.getProjectionSource(query);
  }

  override getSearchCandidateRowIds(
    rowIds: readonly string[],
    searchText: string,
    propertyIds: readonly string[]
  ): readonly string[] {
    return this.runtime.getSearchCandidateRowIds(
      rowIds,
      searchText,
      propertyIds
    );
  }

  override getCellsWindow(
    request: DataSourceCellsWindowRequest
  ): DataSourceCellsWindow {
    return this.runtime.getCellsWindow(request);
  }

  override getLogicalRowIdsSnapshot(): string[] {
    return this.runtime.getLogicalRowIdsSnapshot();
  }

  override rowExists(rowId: string): boolean {
    return this.runtime.rowExists(rowId);
  }

  cellValueChange(
    rowId: string,
    propertyId: string,
    value: unknown,
    actor: DataSourceWriteActor = 'user'
  ): void {
    assertNoAtomicRowMutation(this.runtime);
    this.withLimitNotice(actor, () =>
      this.runtime.cellValueChange(rowId, propertyId, value, actor)
    );
  }

  /**
   * Applies one logical row update as one row-shard transaction. Consumers
   * must not observe a partially written row while a multi-field form save is
   * propagating between independent data-source instances.
   */
  cellValuesChange(
    rowId: string,
    values: Readonly<Record<string, unknown>>,
    actor: DataSourceWriteActor = 'user'
  ): void {
    assertNoAtomicRowMutation(this.runtime);
    this.withLimitNotice(actor, () =>
      this.runtime.cellValuesChange(rowId, values, actor)
    );
  }

  /**
   * A user edit beyond a database limit is rejected before any Yjs write and
   * reported as a toast; the stored value stays unchanged. Non-user writers
   * and hosts without a notification service fail loud.
   */
  private withLimitNotice(
    actor: DataSourceWriteActor,
    write: () => void
  ): void {
    try {
      write();
    } catch (error) {
      if (!this.notifyLimit(actor, error)) throw error;
    }
  }

  /** Shows a rejected local write as a toast; false without notifications. */
  private reportPublicationRejection(error: unknown): boolean {
    // A write cancelled because this source closed has nobody to tell.
    if (
      this._disposed &&
      error instanceof PublicationError &&
      error.code === 'CANCELLED'
    )
      return true;
    if (this.notifyLimit('user', error)) return true;
    const notification = this.serviceGet(NotificationProvider);
    if (!notification) return false;
    notification.toast(dataViewI18nT('database.bulk.failure.WRITE_FAILED'));
    return true;
  }

  /** Shows a user's limit error as a toast; false when it is not one. */
  private notifyLimit(actor: DataSourceWriteActor, error: unknown): boolean {
    const notification =
      actor === 'user' ? this.serviceGet(NotificationProvider) : null;
    if (!notification || !isDatabaseLimitError(error)) return false;
    notification.toast(databaseLimitNoticeText(error.detail));
    return true;
  }

  /**
   * sl-d7zo.7.4: Sharded local-first cell-value write path.
   *
   * 1. Normalise the incoming value through the property meta (so
   *    select/multi-select option lookups + schema-locked guards still
   *    fire — same logic as the local path).
   * 2. Write the owning row-shard subdoc as the single interactive source of
   *    truth. nbstore sync persists that local Yjs update.
   *
   * The GraphQL mutation path is intentionally not used here. Sending a second
   * server-authoritative write for every keystroke races remote echoes against
   * newer local text input and can roll back the last typed character.
   */

  cellValueGet(rowId: string, propertyId: string): unknown {
    return this.runtime.cellValueGet(rowId, propertyId);
  }

  propertyAdd(
    insertToPosition: InsertToPosition,
    ops?: Parameters<DataSourceBase['propertyAdd']>[1]
  ): string | undefined {
    let propertyId: string | undefined;
    this.withLimitNotice('user', () => {
      propertyId = this.runtime.propertyAdd(insertToPosition, ops);
    });
    return propertyId;
  }

  protected override getNormalPropertyAndIndex(propertyId: string):
    | {
        column: ColumnDataType<Record<string, unknown>>;
        index: number;
      }
    | undefined {
    return this.runtime.getNormalPropertyAndIndex(propertyId);
  }

  propertyDataGet(propertyId: string): Record<string, unknown> {
    return this.runtime.propertyDataGet(propertyId);
  }

  propertyDataSet(propertyId: string, data: Record<string, unknown>): void {
    this.withLimitNotice('user', () =>
      this.runtime.propertyDataSet(propertyId, data)
    );
  }

  propertyDataTypeGet(propertyId: string): TypeInstance | undefined {
    return this.runtime.propertyDataTypeGet(propertyId);
  }

  propertyDelete(id: string): void {
    return this.runtime.propertyDelete(id);
  }

  propertyDuplicate(
    propertyId: string,
    options: PropertyDuplicateOptions
  ): string | undefined {
    let duplicateId: string | undefined;
    this.withLimitNotice('user', () => {
      duplicateId = this.runtime.propertyDuplicate(propertyId, options);
    });
    return duplicateId;
  }

  /** sl-web-9n8: Block column operations when schema is locked */

  override propertyCanDelete(propertyId: string): boolean {
    return this.runtime.propertyCanDelete(propertyId);
  }

  override propertyCanDuplicate(propertyId: string): boolean {
    return this.runtime.propertyCanDuplicate(propertyId);
  }

  override propertyTypeCanSet(propertyId: string): boolean {
    return this.runtime.propertyTypeCanSet(propertyId);
  }

  propertyMetaGet(type: string): PropertyMetaConfig | undefined {
    return this.runtime.propertyMetaGet(type);
  }

  propertyNameGet(propertyId: string): string {
    return this.runtime.propertyNameGet(propertyId);
  }

  propertyNameSet(propertyId: string, name: string): void {
    return this.runtime.propertyNameSet(propertyId, name);
  }

  override propertyReadonlyGet(propertyId: string): boolean {
    return this.runtime.propertyReadonlyGet(propertyId);
  }

  override propertyValueReadonlyGet(propertyId: string): boolean {
    if (
      this.schemaLocked$.value &&
      isSystemManagedPropertyData(this.propertyDataGet(propertyId))
    ) {
      const readonlyProperties = new Set([
        'type',
        'created-time',
        'created-by',
        'updated-time',
        'updated-by',
      ]);
      const propertyType = this.propertyTypeGet(propertyId);
      return (
        readonlyProperties.has(propertyId) ||
        (propertyType != null && readonlyProperties.has(propertyType))
      );
    }
    return this.propertyReadonlyGet(propertyId);
  }

  propertyTypeGet(propertyId: string): string | undefined {
    return this.runtime.propertyTypeGet(propertyId);
  }

  propertyTypeSet(propertyId: string, toType: string): void | Promise<void> {
    let pending: void | Promise<void>;
    try {
      pending = this.runtime.propertyTypeSet(propertyId, toType);
    } catch (error) {
      if (!this.notifyTypeChangeRefused(error)) throw error;
      return;
    }
    if (!pending) return;
    // Fail closed: the column keeps its type when the change is refused.
    return pending.catch(error => {
      if (!this.notifyTypeChangeRefused(error)) throw error;
    });
  }

  /** Shows why a column type change was refused; the column keeps its type. */
  private notifyTypeChangeRefused(error: unknown): boolean {
    const notification = this.serviceGet(NotificationProvider);
    if (!notification) return false;
    if (error instanceof DatabaseTablePartsUnavailableError) {
      notification.toast(
        dataViewI18nT('database.typeChange.tablePartsUnavailable')
      );
      return true;
    }
    if (error instanceof DatabaseTypeChangeValuesTooLargeError) {
      notification.toast(dataViewI18nT('database.typeChange.valuesTooLarge'));
      return true;
    }
    if (error instanceof DatabaseColumnCellKeyCollisionError) {
      notification.toast(dataViewI18nT('database.typeChange.cellKeyCollision'));
      return true;
    }
    if (isDatabaseLimitError(error)) {
      notification.toast(databaseLimitNoticeText(error.detail));
      return true;
    }
    return false;
  }

  /**
   * The distinct option names of a text column across every table part, for
   * a change to `toType`. The worker reads the parts that are not loaded and
   * writes none of them. More names than a column may offer come back as
   * they are: the option limit refuses the change.
   */
  async readTableOptionNames(
    columnId: string,
    toType: string,
    signal: AbortSignal = new AbortController().signal
  ): Promise<readonly string[]> {
    const controller = this.getProjectionController();
    if (!controller)
      throw new Error('Reading every table part needs the database worker');
    // The worker reads stored snapshots: local row writes land there first.
    await this.flushRowWrites(signal);
    const { names, everyShardRead, overBudget } = await controller.optionNames(
      columnId,
      toType,
      signal
    );
    if (overBudget) throw new DatabaseTypeChangeValuesTooLargeError();
    if (names.length > DATABASE_PRODUCT_LIMITS.columnOptionHardLimit)
      return names;
    if (!everyShardRead) throw new DatabaseTablePartsUnavailableError();
    return names;
  }

  /**
   * Views use the new row id at once, so a row beyond the limit still
   * throws after the user saw the limit notice.
   */
  rowAdd(insertPosition: InsertToPosition | number): string {
    assertNoAtomicRowMutation(this.runtime);
    try {
      return this.runtime.rowAdd(insertPosition);
    } catch (error) {
      this.notifyLimit('user', error);
      throw error;
    }
  }

  private getBulkPort(): DatabaseClientBulkPort | undefined {
    return (
      databaseBulkProviders.get(this._hostModel.store) ??
      databaseBulkProviders.get(this._hostModel.store.workspace)
    );
  }
  private _bulkAbort?: AbortController;
  private readonly _bulkReceiptOwner = new DatabaseClientBulkReceiptOwner(
    result => {
      if (!this._disposed) this.bulkState$.value = result;
    }
  );
  readonly bulkState$ = signal<
    | (DatabaseBulkProgress & {
        status: 'running' | DatabaseBulkResult['status'];
      })
    | undefined
  >(undefined);

  async bulkMutate(
    request: DatabaseClientBulkRequest
  ): Promise<DatabaseBulkResult> {
    // Resolve an earlier admitted operation before accepting another user action.
    // This call returns that operation, rather than replaying the requested rows.
    const retained = await this._bulkReceiptOwner.refresh();
    if (retained) return retained;
    const operationId = globalThis.crypto.randomUUID();
    const port = this.getBulkPort();
    const initial: DatabaseBulkProgress = {
      operationId,
      databaseId: this.databaseId,
      kind: request.kind,
      iteratorExhausted: false,
      acknowledgement: port?.mode === 'local' ? 'local-storage' : 'doc-persist',
      confirmedCount: 0,
      skippedChanged: 0,
      skippedMissing: 0,
      receipt: null,
    };
    // Each source owns one active user operation. This does not queue/replay work.
    if (!port || this._bulkAbort || this._disposed) {
      const result: DatabaseBulkResult = {
        ...initial,
        status: 'partial',
        error: {
          code: 'WRITE_FAILED',
          message: 'Database operation is unavailable.',
          retryable: false,
        },
      };
      if (!this._bulkAbort) this.bulkState$.value = result;
      return result;
    }
    const abort = new AbortController();
    this._bulkAbort = abort;
    const onAbort = () => abort.abort();
    request.signal?.addEventListener('abort', onAbort, { once: true });
    if (request.signal?.aborted) onAbort();
    this.bulkState$.value = { ...initial, status: 'running' };
    try {
      let values = request.values;
      if (request.kind === 'update') {
        values = Object.fromEntries(
          Object.entries(values ?? {}).map(([id, value]) => {
            if (this.propertyValueReadonlyGet(id))
              throw new Error('This column is read-only.');
            const type = this.propertyTypeGet(id);
            if (!type) throw new Error('The column no longer exists.');
            return [
              id,
              port.mode === 'cloud'
                ? this.runtime.serializeRawValueForProperty(
                    type,
                    this.propertyDataGet(id),
                    value
                  )
                : value,
            ];
          })
        );
      }
      const result = await executeDatabaseClientBulk(
        {
          ...request,
          values,
          signal: abort.signal,
          onProgress: progress => {
            this.bulkState$.value = { ...progress, status: 'running' };
            request.onProgress?.(progress);
          },
        },
        {
          operationId,
          databaseId: this.databaseId,
          deadlineAt: Date.now() + 5000,
          port:
            port.mode === 'cloud'
              ? {
                  mode: 'cloud',
                  mutate: input =>
                    port.mutate({
                      ...input,
                      accessDocId: this._hostModel.store.id,
                    }),
                }
              : {
                  mode: 'local',
                  acknowledge: async (docIds, signal) => {
                    // A write can create split targets absent from prepare's
                    // old row addresses. Confirm the actual write set first.
                    await this.flushRowWrites(signal);
                    await port.acknowledge(docIds, signal);
                  },
                },
          assertCanWrite: () => {
            if (this._disposed || this.readonly$.value)
              throw new Error('Database is read-only.');
            if (request.kind === 'delete') {
              this.runtime.assertFileIndexRowMutationAllowed(
                'user',
                'Row deletion'
              );
              if (this.systemManaged$.value && !this.allowDelete$.value)
                throw new Error('Row deletion is unavailable.');
            } else
              for (const id of Object.keys(values ?? {})) {
                this.runtime.assertFileIndexCellMutationAllowed(id, 'user');
                if (this.propertyValueReadonlyGet(id))
                  throw new Error('Column is read-only.');
              }
          },
          prepare: async (rowIds, signal) => {
            const routed: ReadonlyMap<
              string,
              readonly DatabaseShardManifestEntry[]
            > = findLocalRowShards.call(this.runtime, rowIds);
            const docIds = [
              ...new Set([
                this._model.store.id,
                ...[...routed.values()].flatMap(shards =>
                  shards.map(shard => shard.serverDocId)
                ),
              ]),
            ];
            if (port.mode !== 'local')
              throw new Error('Local storage is unavailable.');
            // Oversized document probes retain no payload; the facade subdivides first.
            if (docIds.length > 64)
              return { rowIds, docIds, release: () => {} };
            const owner = `bulk:${operationId}`;
            setWindowShardDocuments(
              this.runtime,
              owner,
              new Set(
                [...routed.values()].flatMap(shards =>
                  shards.map(shard => shard.serverDocId)
                )
              )
            );
            let releaseLease = () => {};
            let released = false;
            const release = () => {
              if (released) return;
              released = true;
              try {
                releaseWindowShardDocuments(this.runtime, owner);
              } finally {
                releaseLease();
              }
            };
            try {
              releaseLease = await port.retain(docIds, signal);
              if (!signal.aborted && !this._disposed)
                this.runtime.resolveLocalRows(rowIds, 'row-window');
              return { rowIds, docIds, release };
            } catch (error) {
              release();
              throw error;
            }
          },
          write: async (rowIds, input, signal) => {
            const scope = openPublicationScope(this.runtime);
            try {
              if (input.kind === 'delete')
                this.runtime.rowDelete([...rowIds], 'user');
              else
                writeShardedRowCellsToLocalDocs.call(
                  this.runtime,
                  rowIds,
                  input.values ?? {}
                );
            } finally {
              await scope.settle(signal);
            }
          },
        }
      );
      this.bulkState$.value = result;
      if (port.mode === 'cloud')
        this._bulkReceiptOwner.remember(result, port, {
          databaseId: this.databaseId,
          accessDocId: this._hostModel.store.id,
        });
      return result;
    } catch (error) {
      const result: DatabaseBulkResult = {
        ...initial,
        status: 'partial',
        error: {
          code: 'INVALID_INPUT',
          message:
            error instanceof Error ? error.message : 'Invalid row operation.',
          retryable: false,
        },
      };
      this.bulkState$.value = result;
      return result;
    } finally {
      request.signal?.removeEventListener('abort', onAbort);
      this._bulkAbort = undefined;
    }
  }

  rowDelete(ids: string[], actor: DataSourceWriteActor = 'user'): void {
    assertNoAtomicRowMutation(this.runtime);
    if (actor === 'user' && this.getBulkPort()) {
      void this.bulkMutate({ kind: 'delete', rowIds: ids });
      return;
    }
    return this.runtime.rowDelete(ids, actor);
  }

  rowMove(rowId: string, position: InsertToPosition): void {
    assertNoAtomicRowMutation(this.runtime);
    return this.runtime.rowMove(rowId, position);
  }

  viewDataAdd(viewData: DataViewDataType): string {
    return this.runtime.viewDataAdd(viewData);
  }

  viewDataDelete(viewId: string): void {
    return this.runtime.viewDataDelete(viewId);
  }

  viewDataDuplicate(id: string): string {
    return this.runtime.viewDataDuplicate(id);
  }

  viewDataGet(viewId: string): DataViewDataType | undefined {
    return this.runtime.viewDataGet(viewId);
  }

  viewDataMoveTo(id: string, position: InsertToPosition): void {
    return this.runtime.viewDataMoveTo(id, position);
  }

  viewDataUpdate<ViewData extends DataViewDataType>(
    id: string,
    updater: (data: ViewData) => Partial<ViewData>
  ): void {
    return this.runtime.viewDataUpdate(id, updater);
  }

  viewMetaGet(type: string): ViewMeta {
    return this.runtime.viewMetaGet(type);
  }

  viewMetaGetById(viewId: string): ViewMeta | undefined {
    return this.runtime.viewMetaGetById(viewId);
  }
}

export function createDatabaseBlockDataSource(
  model: DatabaseBlockModel,
  init?: (dataSource: DatabaseBlockDataSource) => void
): DatabaseBlockDataSource {
  return new DatabaseBlockDataSource(model, init);
}

export async function provisionShardedDatabaseBlockDataSource(
  hostDoc: Store,
  input: ShardedDatabaseBlockDataSourceProvisionInput
): Promise<ShardedDatabaseBlockDataSourceProvisionResult> {
  const provisioned = await provisionShardedDatabaseHostBlock(
    hostDoc,
    input,
    (model, { created }) => {
      const staged = createDatabaseBlockDataSource(model);
      try {
        input.initDataSource?.(staged);
        if (created && input.initialEmptyRow) staged.rowAdd('end');
        // A database created with a view opens on the canvas as that view,
        // not as the summary card of a view-less database. An existing root
        // keeps the presentation its users left it in.
        const [firstView] = model.props.views;
        if (created && firstView)
          staged.canvasPresentationSet(
            createCanvasLivePresentationState(firstView.id)
          );
      } finally {
        staged.dispose();
      }
    }
  );
  if (provisioned.status === 'discarded') return provisioned;
  return {
    ...provisioned,
    dataSource: createDatabaseBlockDataSource(provisioned.model),
  };
}

export const databaseViewInitTemplate = (
  datasource: DatabaseBlockDataSource,
  viewType: string
) => {
  datasource.viewManager.viewAdd(viewType);
};
export const convertToDatabase = async (host: EditorHost, viewType: string) => {
  const [_, ctx] = host.std.command.exec(getSelectedModelsCommand, {
    types: ['block', 'text'],
  });
  const { selectedModels } = ctx;
  const firstModel = selectedModels?.[0];
  if (!firstModel) return;

  host.store.captureSync();

  const parentModel = host.store.getParent(firstModel);
  if (!parentModel) {
    return;
  }

  assertDatabaseColumnShapeWithinLimits({
    source: 'Converted database',
    columnCount: 1,
  });
  if (selectedModels.length > 0) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Converted database cannot move selected blocks into database rows; ${ROW_SHARDED_DATABASE_GATE_MESSAGE}.`
    );
  }

  const provisioned = await provisionShardedDatabaseBlockDataSource(
    host.store,
    {
      addBlock: (_flavour, props) =>
        host.store.addBlock(
          'affine:database',
          props,
          parentModel,
          parentModel.children.indexOf(firstModel)
        ),
      initDataSource: dataSource => dataSource.viewManager.viewAdd(viewType),
    }
  );
  if (provisioned.status === 'discarded')
    throw new PublicationError('CANCELLED');
  const databaseModel = provisioned.model;
  host.store.moveBlocks(selectedModels, databaseModel);

  const selectionManager = host.selection;
  selectionManager.clear();
};
